import { Controller, useForm } from "react-hook-form";
import { useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { Badge2 } from "@/ui2/atoms/Badge";
import { Button2 } from "@/components/UI2/atoms/Button";
import type { CosignatureDocumentType, UUID } from "@/core/types";
import { ModalControlled2, ModalDivider } from "@/components/UI2/molecules/Modal";
import { TextField2 } from "@/components/UI2/molecules/TextField";
import { useRejectDocument } from "@/features/cosignature/api/mutations/useRejectDocument";

import { rejectNoteSchema, type RejectNoteFormValues } from "./schema";

type RejectNoteModalProps = {
  documentId: string;
  documentType: CosignatureDocumentType;
  isOpen: boolean;
  noteId: UUID;
  onClose: () => void;
  onSuccess?: VoidFunction;
  patientId: string;
  providerName: string;
};

export function RejectNoteModal({
  documentId,
  documentType,
  isOpen,
  noteId,
  onClose,
  onSuccess,
  patientId,
  providerName,
}: RejectNoteModalProps) {
  const { control, formState, handleSubmit, reset } = useForm<RejectNoteFormValues>({
    defaultValues: { rejectionReason: "" },
    mode: "onChange",
    resolver: zodResolver(rejectNoteSchema),
  });

  const { isPending, mutate } = useRejectDocument({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <ModalControlled2
      isOpen={isOpen}
      onOpenChange={handleClose}
      title={`Reject ${documentType === "note_addendum" ? "Addendum" : "Note"}`}
    >
      <form
        className="tw:flex tw:flex-col tw:gap-4"
        onSubmit={handleSubmit(({ rejectionReason }) =>
          mutate({ documentId, documentType, noteId, patientId, rejectionReason }),
        )}
      >
        <p className="tw:text-text-secondary">
          {documentType === "note_addendum" ? (
            <>
              Please provide a detailed comment explaining why this addendum is being rejected.{" "}
              <b>{providerName}</b> will receive this feedback and the addendum will be returned for
              revision.
            </>
          ) : (
            <>
              Please provide a detailed comment explaining why this note is being rejected.{" "}
              <b>{providerName}</b> will receive this feedback and the note will be returned to{" "}
              <Badge2 appearance="outline" className="tw:mx-ch" size="xs" variant="bright-purple">
                Draft
              </Badge2>{" "}
              status.
            </>
          )}
        </p>

        <Controller
          control={control}
          name="rejectionReason"
          render={({ field, fieldState }) => (
            <TextField2
              errorMessage={fieldState.error?.message}
              hasStrongLabel
              isInvalid={!!fieldState.error}
              label="Rejection reason"
              mode="textarea"
              onChange={field.onChange}
              placeholder="Explain what needs to be corrected or added…"
              textAreaProps={{ className: "tw:min-h-24" }}
              value={field.value}
            />
          )}
        />

        <ModalDivider />

        <div className="tw:flex tw:justify-end tw:gap-3">
          <Button2 onPress={handleClose} variant="secondary">
            Cancel
          </Button2>

          <Button2
            isDisabled={!formState.isValid}
            isLoading={isPending}
            type="submit"
            variant="primary"
          >
            Reject {documentType === "note_addendum" ? "Addendum" : "Note"}
          </Button2>
        </div>
      </form>
    </ModalControlled2>
  );
}
